import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import { StreakService } from '../../services/StreakService';
import { XpService, DEFAULT_GOAL } from '../../services/XpService';
import { SrsService } from '../../services/SrsService';
import { AchievementsService, BADGES } from '../../services/AchievementsService';
import { LearningProgressService } from '../../services/LearningProgressService';
import { ShareCardService } from '../../services/ShareCardService';

function Metric({ icon, value, label }) {
  return (
    <div className="flex flex-col items-center gap-1 rounded-2xl border border-slate-200 bg-white py-4 shadow-sm">
      <span className="text-xl leading-none">{icon}</span>
      <span className="text-lg font-black text-slate-900">{value}</span>
      <span className="text-center text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</span>
    </div>
  );
}

function Badge({ badge, unlocked }) {
  const { t } = useTranslation();
  const [sharing, setSharing] = useState(false);

  async function handleShare(e) {
    e.stopPropagation();
    if (sharing) return;
    setSharing(true);
    try {
      const title = t(badge.title);
      const blob = await ShareCardService.generateBadgeCard(badge, title);
      await ShareCardService.shareOrDownload(blob, `sublingox-${badge.title.split('.').pop()}.png`, t('achievements.shareText', { badge: title }));
    } finally {
      setSharing(false);
    }
  }

  return (
    <div
      className={`relative flex w-24 flex-col items-center gap-1 rounded-2xl border border-slate-200 bg-white p-3 text-center transition ${
        unlocked ? '' : 'opacity-30'
      }`}
    >
      <span className="text-2xl leading-none">{badge.emoji}</span>
      <span className="text-[11px] font-bold leading-tight text-slate-700">{t(badge.title)}</span>
      {unlocked && (
        <button
          onClick={handleShare}
          disabled={sharing}
          aria-label={t('achievements.share')}
          className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full border border-slate-200 bg-white text-xs shadow-sm transition hover:bg-slate-50 disabled:opacity-50"
        >
          📤
        </button>
      )}
    </div>
  );
}

function GoalSheet({ initialGoal, onClose, onSave }) {
  const { t } = useTranslation();
  const [goal, setGoal] = useState(initialGoal);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 pb-6 backdrop-blur-sm sm:items-center sm:pb-0"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-black text-slate-900">{t('progress.goalSettings')}</h2>
        <p className="mt-1 text-sm text-slate-500">{t('progress.chooseGoal')}</p>
        <div className="mt-4 flex gap-2">
          {[10, 20, 40].map((value) => (
            <button
              key={value}
              onClick={() => setGoal(value)}
              className={`flex-1 rounded-xl border py-2.5 text-sm font-bold transition ${
                goal === value
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                  : 'border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              {value} XP
            </button>
          ))}
        </div>
        <button
          onClick={() => onSave(goal)}
          className="mt-5 w-full rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 py-2.5 text-sm font-bold text-white shadow-md transition hover:-translate-y-0.5"
        >
          {t('progress.savePreferences')}
        </button>
      </div>
    </div>
  );
}

export default function ProgressPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { languagePrefs, isAuthenticated } = useAuth();
  const config = useAppConfig(languagePrefs?.nativeLanguage);

  const [streak, setStreak] = useState(0);
  const [freezes, setFreezes] = useState(0);
  const [todayXp, setTodayXp] = useState(0);
  const [totalXp, setTotalXp] = useState(0);
  const [goal, setGoal] = useState(DEFAULT_GOAL);
  const [due, setDue] = useState(0);
  const [unlocked, setUnlocked] = useState(new Set());
  const [showGoalSheet, setShowGoalSheet] = useState(false);
  const langPair = languagePrefs
    ? `${languagePrefs.nativeLanguage}_${languagePrefs.targetLanguage}`
    : 'guest';
  const [pathProgress, setPathProgress] = useState(() => LearningProgressService.snapshot(langPair));

  function refresh() {
    setStreak(StreakService.getStreak());
    setFreezes(StreakService.getFreezes());
    setTodayXp(XpService.getTodayXp());
    setTotalXp(XpService.getTotalXp());
    setGoal(XpService.getGoal());
    setUnlocked(AchievementsService.getUnlocked());
    setPathProgress(LearningProgressService.snapshot(langPair));
    setDue(
      languagePrefs
        ? SrsService.getDueCount(`${languagePrefs.nativeLanguage}_${languagePrefs.targetLanguage}`)
        : 0
    );
  }

  useEffect(refresh, [languagePrefs, langPair]);

  const progress = goal === 0 ? 0 : Math.min(1, todayXp / goal);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 px-4 py-10">
      <div className="mx-auto max-w-lg space-y-5">
        <button
          onClick={() => navigate('/profile')}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-slate-700"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          {t('profile.backToHome')}
        </button>

        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-black text-slate-900">{t('progress.title')}</h1>
          <button
            onClick={() => setShowGoalSheet(true)}
            title={t('progress.goalSettings')}
            className="rounded-xl border border-slate-200 bg-white p-2.5 text-slate-500 shadow-sm transition hover:border-indigo-300 hover:text-indigo-600"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>
        </div>

        {!isAuthenticated && (
          <div className="flex flex-col gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 sm:flex-row sm:items-center sm:justify-between">
            <span>{t('progress.guestBanner')}</span>
            <button
              onClick={() => navigate('/login')}
              className="shrink-0 font-bold text-amber-900 underline underline-offset-2 hover:no-underline"
            >
              {t('progress.guestBannerCta')}
            </button>
          </div>
        )}

        {/* Today's XP hero */}
        <div className="rounded-3xl bg-gradient-to-r from-indigo-600 to-violet-600 p-6 text-white shadow-xl">
          <p className="text-xs font-bold uppercase tracking-wide text-white/70">{t('progress.todaysLearning')}</p>
          <p className="mt-2 text-3xl font-black">{todayXp} / {goal} XP</p>
          <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/25">
            <div
              className="h-full rounded-full bg-white transition-all duration-700"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-3 gap-3">
          <Metric icon={streak > 0 ? '🔥' : '💤'} value={streak} label={t('progress.dayStreak')} />
          <Metric icon="🧠" value={due} label={t('progress.cardsDue')} />
          <Metric icon="⭐" value={totalXp} label={t('progress.totalXp')} />
        </div>

        {streak > 0 && (
          <p className="text-center text-xs font-semibold text-slate-400">
            ❄️ {t('progress.streakFreezes', { count: freezes })}
          </p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate('/leaderboard')}
            className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:border-indigo-300 hover:text-indigo-600"
          >
            🏆 {t('leaderboard.title')}
          </button>
          <button
            onClick={() => navigate('/friends')}
            className="flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:border-indigo-300 hover:text-indigo-600"
          >
            👥 {t('friends.title')}
          </button>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-4">
            <div className="relative grid h-20 w-20 place-items-center rounded-full" style={{ background: `conic-gradient(#4f46e5 ${pathProgress.overall * 3.6}deg, #e2e8f0 0deg)` }}>
              <div className="grid h-14 w-14 place-items-center rounded-full bg-white text-sm font-black text-indigo-700">{pathProgress.overall}%</div>
            </div>
            <div><h2 className="font-black text-slate-900">{t('progress.tenStepMastery')}</h2><p className="text-sm font-bold text-indigo-600">{pathProgress.mastery}</p><p className="text-xs text-slate-400">{t('progress.pathDesc')}</p></div>
          </div>
          <div className="space-y-3">
            {config.learningSteps.map((step) => {
              const value = pathProgress.steps[step.code];
              if (!value) return null;
              return <div key={step.code}><div className="mb-1 flex justify-between text-xs"><span className="font-bold text-slate-700">{step.label}</span><span className="text-slate-400">{value.mastery} · {value.percent}%</span></div><div className="h-2 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500" style={{ width: `${value.percent}%` }} /></div></div>;
            })}
          </div>
        </div>

        {/* Achievements */}
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="mb-3 text-sm font-black uppercase tracking-widest text-slate-400">
            {t('progress.achievements')}
          </h2>
          <div className="flex flex-wrap gap-3">
            {Object.entries(BADGES).map(([id, badge]) => (
              <Badge key={id} badge={badge} unlocked={unlocked.has(id)} />
            ))}
          </div>
        </div>
      </div>

      {showGoalSheet && (
        <GoalSheet
          initialGoal={goal}
          onClose={() => setShowGoalSheet(false)}
          onSave={(g) => {
            XpService.setGoal(g);
            setGoal(g);
            setShowGoalSheet(false);
          }}
        />
      )}
    </div>
  );
}
